import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { PageHeader } from '../components/page-header';
import { NewVacationRequestForm, NewVacationRequestValues } from '../components/vacations';
import { useCreateVacationRequest } from '../hooks';

const NewVacationRequest: React.FC = () => {
  const { t } = useTranslation('vacations');
  const navigate = useNavigate();
  const { user } = useAuth();
  const createRequest = useCreateVacationRequest();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (values: NewVacationRequestValues) => {
    setError(null);
    try {
      await createRequest.mutateAsync({
        memberId: user?.id || '',
        startDate: values.startDate,
        endDate: values.endDate,
        notes: values.notes || undefined,
      });
      setSuccess(true);
      setTimeout(() => navigate('/vacations'), 1200);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        t('form.error.submitFailed');
      setError(message || null);
    }
  };

  return (
    <div data-testid="new-vacation-page">
      <PageHeader title={t('form.title')} description={t('form.description')} testId="new-vacation-header" />

      <main className="container mx-auto px-4 py-8 max-w-2xl">
        <Card data-testid="new-vacation-card">
          <CardHeader>
            <CardTitle>{t('form.title')}</CardTitle>
            <CardDescription>{t('form.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            {success && (
              <Alert variant="success" className="mb-4" data-testid="new-vacation-success">
                <AlertDescription>{t('form.success')}</AlertDescription>
              </Alert>
            )}
            <NewVacationRequestForm
              isSubmitting={createRequest.isPending}
              serverError={error}
              onSubmit={handleSubmit}
              onCancel={() => navigate('/vacations')}
            />
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default NewVacationRequest;
